import React, { useState, useMemo, useCallback } from 'react';
import { DB } from '../data/database';
import { PARTS_CATALOG, PARTS_CATALOG_CATEGORIES } from '../data/partsCatalog';
import { SearchIcon, CheckIcon } from './Icons';

// ── Data helpers ─────────────────────────────────────────────
function getCatalog() {
  return DB.get('partsCatalog') || PARTS_CATALOG;
}

function groupByCategory(catalog) {
  const grouped = {};
  PARTS_CATALOG_CATEGORIES.forEach(cat => { grouped[cat] = []; });
  catalog.forEach(part => {
    if (!grouped[part.category]) grouped[part.category] = [];
    grouped[part.category].push(part);
  });
  Object.values(grouped).forEach(parts =>
    parts.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
  );
  Object.keys(grouped).forEach(cat => {
    if (grouped[cat].length === 0) delete grouped[cat];
  });
  return grouped;
}

// ── Stable quantity input (memo'd to prevent focus loss) ─────
const QtyInput = React.memo(function QtyInput({ partName, qty, onChange }) {
  return (
    <input
      type="number"
      min={1} max={99}
      value={qty}
      onClick={e => e.stopPropagation()}
      onChange={e => onChange(partName, Math.max(1, Math.min(99, parseInt(e.target.value) || 1)))}
      className="ps-qty-input"
    />
  );
});

// ── Main PartsSelector Component ─────────────────────────────
// Props:
//   onConfirm(selectedParts[]) — [{part_name, category, quantity, catalog_id}]
//   onCancel()                 — close
//   mode                       — 'inventory' | 'request'
//   vehicleInfo                — optional {make,model,year,sku,chassis,images}
//   initialSelection           — optional [{part_name, quantity}] to pre-fill
function PartsSelector({ onConfirm, onCancel, mode = 'request', vehicleInfo, initialSelection, allowedCategories }) {
  const fullCatalog = useMemo(getCatalog, []);
  // Filter catalog by allowed categories if specified
  const catalog = useMemo(() => {
    if (!allowedCategories || allowedCategories.length === 0) return fullCatalog;
    return fullCatalog.filter(p => allowedCategories.includes(p.category));
  }, [fullCatalog, allowedCategories]);
  const grouped = useMemo(() => groupByCategory(catalog), [catalog]);
  const commonParts = useMemo(() => catalog.filter(p => p.is_common), [catalog]);

  // Selection: Map<part_name → { part_name, category, quantity, catalog_id }>
  const [selected, setSelected] = useState(() => {
    const map = new Map();
    if (initialSelection) {
      initialSelection.forEach(s => {
        const cat = catalog.find(c => c.part_name === s.part_name);
        map.set(s.part_name, {
          part_name: s.part_name,
          category: cat?.category || '',
          quantity: s.quantity || 1,
          catalog_id: cat?.id || null,
        });
      });
    }
    return map;
  });

  const [search, setSearch]       = useState('');
  const [expandedCat, setExpandedCat] = useState(null);

  // ── Search filtering ──
  const q = search.toLowerCase();
  const filteredGrouped = useMemo(() => {
    if (!q) return grouped;
    const result = {};
    Object.entries(grouped).forEach(([cat, parts]) => {
      const matches = parts.filter(p => p.part_name.toLowerCase().includes(q));
      if (matches.length > 0) result[cat] = matches;
    });
    return result;
  }, [grouped, q]);

  const categories = Object.keys(filteredGrouped);
  const isSearching = q.length > 0;

  // ── Selection helpers ──
  const togglePart = useCallback((part) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(part.part_name)) {
        next.delete(part.part_name);
      } else {
        next.set(part.part_name, {
          part_name: part.part_name,
          category: part.category,
          quantity: 1,
          catalog_id: part.id || null,
        });
      }
      return next;
    });
  }, []);

  const setQty = useCallback((partName, qty) => {
    setSelected(prev => {
      const next = new Map(prev);
      const entry = next.get(partName);
      if (entry) next.set(partName, { ...entry, quantity: qty });
      return next;
    });
  }, []);

  const removePart = useCallback((partName) => {
    setSelected(prev => {
      const next = new Map(prev);
      next.delete(partName);
      return next;
    });
  }, []);

  const handleConfirm = () => {
    onConfirm([...selected.values()]);
  };

  const selectedArr = [...selected.values()];
  const selectedCount = selectedArr.reduce((s, p) => s + p.quantity, 0);
  const vehicleName = vehicleInfo
    ? `${vehicleInfo.year || ''} ${vehicleInfo.make || ''} ${vehicleInfo.model || ''}`.trim()
    : '';

  return (
    <div className="ps-overlay" onClick={onCancel}>
      <div className="ps-modal" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="ps-header">
          <div>
            <div className="ps-title">Select Parts</div>
            {vehicleInfo && (
              <div className="ps-vehicle-info">
                From: <strong>{vehicleName}</strong>
                {vehicleInfo.sku && <span className="ps-vehicle-sku">{vehicleInfo.sku}</span>}
              </div>
            )}
          </div>
          <button className="ps-close" onClick={onCancel}>×</button>
        </div>

        {/* ── Search (sticky) ── */}
        <div className="ps-search-wrap">
          <div className="ps-search-inner">
            <SearchIcon />
            <input
              type="text"
              className="ps-search-input"
              placeholder="Search parts..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="ps-search-clear" onClick={() => setSearch('')}>×</button>
            )}
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="ps-body">

          {/* ── Quick Select (common parts) ── */}
          {!isSearching && (
            <div className="ps-section">
              <div className="ps-section-label">Quick Select</div>
              <div className="ps-quick-row">
                {commonParts.map(part => {
                  const isActive = selected.has(part.part_name);
                  return (
                    <button
                      key={part.part_name}
                      type="button"
                      className={`ps-quick-pill${isActive ? ' active' : ''}`}
                      onClick={() => togglePart(part)}
                    >
                      {isActive && <span className="ps-quick-check">✓</span>}
                      {part.part_name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Categories ── */}
          <div className="ps-section">
            <div className="ps-section-label">Categories</div>

            {categories.length === 0 && (
              <div className="ps-empty">No parts match "{search}"</div>
            )}

            {categories.map(cat => {
              const isExpanded = isSearching || expandedCat === cat;
              const parts = filteredGrouped[cat];
              const catSelectedCount = parts.filter(p => selected.has(p.part_name)).length;

              return (
                <div key={cat} className="ps-category">
                  <div
                    className={`ps-cat-header${catSelectedCount > 0 ? ' has-selected' : ''}`}
                    onClick={() => {
                      if (!isSearching) setExpandedCat(isExpanded ? null : cat);
                    }}
                  >
                    <span className="ps-cat-chevron">{isExpanded ? '▼' : '▶'}</span>
                    <span className="ps-cat-name">{cat}</span>
                    {catSelectedCount > 0 && (
                      <span className="ps-cat-badge">{catSelectedCount}</span>
                    )}
                    <span className="ps-cat-count">({parts.length})</span>
                  </div>

                  {isExpanded && (
                    <div className="ps-cat-items">
                      {parts.map(part => {
                        const sel = selected.get(part.part_name);
                        const isChecked = !!sel;
                        const isHighlight = isSearching && part.part_name.toLowerCase().includes(q);
                        return (
                          <div
                            key={part.part_name}
                            className={`ps-part-row${isChecked ? ' checked' : ''}${isHighlight ? ' highlight' : ''}`}
                            onClick={() => togglePart(part)}
                          >
                            <span className="ps-part-checkbox">{isChecked ? '☑' : '☐'}</span>
                            <span className="ps-part-name">{part.part_name}</span>
                            {isChecked && (
                              <span className="ps-part-qty" onClick={e => e.stopPropagation()}>
                                ×<QtyInput partName={part.part_name} qty={sel.quantity} onChange={setQty} />
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Selected summary (sticky bottom) ── */}
        <div className="ps-footer">
          <div className="ps-footer-label">
            Selected ({selectedArr.length} part{selectedArr.length !== 1 ? 's' : ''}{selectedCount !== selectedArr.length ? `, ${selectedCount} total` : ''})
          </div>

          {selectedArr.length === 0 ? (
            <div className="ps-footer-empty">No parts selected yet</div>
          ) : (
            <div className="ps-footer-list">
              {selectedArr.map(p => (
                <div key={p.part_name} className="ps-footer-item">
                  <span className="ps-footer-item-name">{p.part_name}</span>
                  <span className="ps-footer-item-qty">× {p.quantity}</span>
                  <button className="ps-footer-item-rm" onClick={() => removePart(p.part_name)}>×</button>
                </div>
              ))}
            </div>
          )}

          <div className="ps-footer-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={selectedArr.length === 0}
              onClick={handleConfirm}
            >
              <CheckIcon />
              {mode === 'inventory'
                ? `Add ${selectedArr.length} Part${selectedArr.length !== 1 ? 's' : ''}`
                : `Confirm Selection`}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}

export default PartsSelector;
